import { useContext, useEffect, useRef, useState } from "react";

import config from "@/config";
import ArtworkDataContext from "@/context/ArtworkDataContext";
import useStateHandler from "@/hooks/useStateHandler";

import MarinaTipPopup from "./components/MarinaTipPopup";
import PopupCropTemplateImage from "./components/PopupCropTemplateImage";
import PopupDownloadSuccessful from "./components/PopupDownloadSuccessful";
import PopupSelectDesign from "./components/PopupSelectDesign";
import PopupSelectTemplate from "./components/PopupSelectTemplate";
import PopupTemplatePreview from "./components/PopupTemplatePreview";
import TemplatePreview from "./components/TemplatePreview";
import { TEMPLATE_DESIGNS, TYPE_ONE_CROP_DIMENSIONS } from "./constants";

import "./index.css";

const DownloadTemplate = ({ open, close, artworkData: artworkDataProp }) => {
  const context = useContext(ArtworkDataContext);
  const artworkData = artworkDataProp || context?.artworkData;
  const { state } = useStateHandler();

  const [activeStep, setActiveStep] = useState("format");
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedMode, setSelectedMode] = useState(
    state.user_session?.user_preferences?.mode ?? "dark"
  );
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [croppedUrl, setCroppedUrl] = useState(null);
  const [croppedFile, setCroppedFile] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccessPopup, setDownloadSuccessPopup] = useState(false);
  const [marinaTipOpen, setMarinaTipOpen] = useState(false);

  const templatePreviewRef = useRef(null);

  const imageId = artworkData?.artwork_media?.artwork_main_picture?.image_id;
  const image = imageId
    ? `${config.app?.imageServiceDomain ?? "https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA"}/${imageId}/public`
    : null;

  const marinaTipVideo =
    artworkData?.artwork_media?.marina_tip_video ||
    artworkData?.artwork_about?.marina_tip_video ||
    artworkData?.artwork_about?.marina_tip_video_url ||
    null;

  const isTypeOneDesign = selectedDesign === TEMPLATE_DESIGNS.TYPE_ONE;

  const resetState = () => {
    setActiveStep("format");
    setSelectedFormat(null);
    setSelectedDesign(null);
    setCroppedFile(null);
    setCroppedUrl(null);
    setIsDownloading(false);
    setDownloadSuccessPopup(false);
    setSelectedMode(state.user_session?.user_preferences?.mode ?? "dark");
    setMarinaTipOpen(false);
  };

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open]);

  const handleCloseFlow = () => {
    resetState();
    close?.();
  };

  const handleContinueFromFormat = () => {
    if (!selectedFormat) {
      return;
    }
    setActiveStep("design");
    setSelectedDesign(null);
  };

  const handleContinueFromDesign = () => {
    if (!selectedDesign) {
      return;
    }

    if (isTypeOneDesign) {
      setActiveStep("crop");
      return;
    }

    setCroppedUrl(image);
    setCroppedFile(null);
    setActiveStep("preview");
  };

  const handleExitCrop = () => {
    handleCloseFlow();
  };

  const handleCropToPreview = () => {
    setActiveStep("preview");
  };

  const handleDownloadCompleted = () => {
    setDownloadSuccessPopup(true);
    setActiveStep("success");
  };

  const handleBackFromDesign = () => {
    setActiveStep("format");
  };

  const handleBackFromCrop = () => {
    setActiveStep("design");
  };

  const handleBackFromPreview = () => {
    if (isTypeOneDesign) {
      setActiveStep("crop");
      return;
    }

    setActiveStep("design");
  };

  if (!artworkData || !imageId) {
    return null;
  }

  return (
    <>
      <PopupSelectTemplate
        open={open && activeStep === "format"}
        onClose={handleCloseFlow}
        onGoBack={handleCloseFlow}
        selectedFormat={selectedFormat}
        onSelectFormat={setSelectedFormat}
        selectedMode={selectedMode}
        onSelectMode={setSelectedMode}
        onContinue={handleContinueFromFormat}
      />

      <PopupSelectDesign
        open={open && activeStep === "design"}
        onClose={handleCloseFlow}
        onGoBack={handleBackFromDesign}
        mode={selectedMode}
        format={selectedFormat}
        selectedDesign={selectedDesign}
        onSelectDesign={setSelectedDesign}
        onContinue={handleContinueFromDesign}
        image={image}
      />

      <PopupCropTemplateImage
        open={activeStep === "crop"}
        image={image}
        selectTemplate={selectedFormat}
        measures={TYPE_ONE_CROP_DIMENSIONS}
        exitCrop={handleExitCrop}
        onGoBack={handleBackFromCrop}
        setCroppedUrl={setCroppedUrl}
        setCroppedFile={setCroppedFile}
        continueCrop={handleCropToPreview}
        stepNumber={3}
      />

      <PopupTemplatePreview
        open={activeStep === "preview"}
        onClose={handleCloseFlow}
        onGoBack={handleBackFromPreview}
        format={selectedFormat}
        mode={selectedMode}
        design={selectedDesign}
        croppedUrl={croppedUrl}
        originalImage={image}
        croppedFile={croppedFile}
        templatePreviewRef={templatePreviewRef}
        isDownloading={isDownloading}
        setIsDownloading={setIsDownloading}
        artworkData={artworkData}
        onShowTip={() => setMarinaTipOpen(true)}
        onDownloadCompleted={handleDownloadCompleted}
      />

      <PopupDownloadSuccessful
        open={downloadSuccessPopup && activeStep === "success"}
        close={() => {
          setDownloadSuccessPopup(false);
          handleCloseFlow();
        }}
      >
        <TemplatePreview
          reference={templatePreviewRef}
          croppedUrl={croppedUrl}
          design={selectedDesign}
          format={selectedFormat}
          mode={selectedMode}
          originalImage={image}
          artworkData={artworkData}
        />
      </PopupDownloadSuccessful>

      <MarinaTipPopup
        open={marinaTipOpen}
        onClose={() => setMarinaTipOpen(false)}
        videoUrl={marinaTipVideo}
      />
    </>
  );
};

export default DownloadTemplate;
